import { useState, useEffect } from 'react'
import { Building, User } from '../types'
import { useAuth } from '../hooks/useAuth'
import { loadBuildings, loadSchoolEmployees, loadUsersByRole, createPropertyMoveTicket } from '../lib/apiPhpAdapter'
import UserAutocomplete from './UserAutocomplete'
import './CreatePropertyMoveTicketForm.css'

interface CreatePropertyMoveTicketFormProps {
  onCancel: () => void
  onSuccess: () => void
}

function CreatePropertyMoveTicketForm({ onCancel, onSuccess }: CreatePropertyMoveTicketFormProps) {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    fromBuildingId: '',
    fromLocation: '',
    itemName: '', // Название предмета
    inventoryNumbers: [''] as string[], // Массив инвентарных номеров
    toBuildingId: '',
    toLocation: '',
    isWarehouse: false,
    assignedTo: null as User | null,
    transportOption: '' as '' | 'self' | 'organize', // Опция транспорта для склада
  })
  const [buildings, setBuildings] = useState<Building[]>([])
  const [propertyReferents, setPropertyReferents] = useState<User[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadFormData()
  }, [])

  const loadFormData = async () => {
    try {
      setLoading(true)
      const buildingsData = await loadBuildings()
      setBuildings(buildingsData)

      const employees = await loadSchoolEmployees()
      const referentUsers = await loadUsersByRole('property_referent')
      const referentUserIds = new Set(referentUsers.map((u) => u.id))
      const referents: User[] = []
      for (const emp of employees) {
        const accountId = emp.user_id ?? (emp.id.startsWith('se-') ? null : emp.id)
        if (!accountId || !referentUserIds.has(accountId)) continue
        referents.push({ ...emp, id: accountId, role: 'property_referent' })
      }
      setPropertyReferents(referents)
    } catch (error) {
      console.error('Chyba při načítání dat:', error)
      alert('Chyba při načítání dat formuláře')
    } finally {
      setLoading(false)
    }
  }
  
  const addInventoryNumberField = () => {
    setFormData({
      ...formData,
      inventoryNumbers: [...formData.inventoryNumbers, '']
    })
  }
  
  const removeInventoryNumberField = (index: number) => {
    if (formData.inventoryNumbers.length > 1) {
      const newNumbers = formData.inventoryNumbers.filter((_, i) => i !== index)
      setFormData({
        ...formData,
        inventoryNumbers: newNumbers
      })
    }
  }
  
  const updateInventoryNumber = (index: number, value: string) => {
    const newNumbers = [...formData.inventoryNumbers]
    newNumbers[index] = value
    setFormData({
      ...formData,
      inventoryNumbers: newNumbers
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Валидация
    if (!formData.fromBuildingId || !formData.fromLocation) {
      alert('Prosím vyplňte všechna povinná pole (odkud, kde konkrétně)')
      return
    }
    
    if (!formData.itemName.trim()) {
      alert('Prosím vyplňte název předmětu')
      return
    }
    
    // Проверяем, что хотя бы одно инвентарное число заполнено
    const validInventoryNumbers = formData.inventoryNumbers.filter(num => num.trim() !== '')
    if (validInventoryNumbers.length === 0) {
      alert('Prosím vyplňte alespoň jedno inventární číslo')
      return
    }

    if (!formData.isWarehouse) {
      // Если не склад, нужны здание и место
      if (!formData.toBuildingId || !formData.toLocation) {
        alert('Prosím vyplňte kam přesunout (budova a konkrétní místo)')
        return
      }
    }
    
    // Поле "Komu předat" всегда должно быть заполнено
    if (!formData.assignedTo) {
      alert('Prosím vyberte komu předat')
      return
    }
    
    // Если склад, проверяем выбор транспорта
    if (formData.isWarehouse && !formData.transportOption) {
      alert('Prosím vyberte způsob dopravy na sklad')
      return
    }

    if (!user) {
      alert('Nejste přihlášeni')
      return
    }

    setSubmitting(true)
    try {
      // Объединяем инвентарные номера в строку (через запятую)
      const inventoryNumberString = validInventoryNumbers.join(', ')
      
      await createPropertyMoveTicket({
        from_building_id: formData.fromBuildingId,
        from_location: formData.fromLocation,
        item_name: formData.itemName.trim(),
        inventory_number: inventoryNumberString,
        to_building_id: formData.isWarehouse ? null : formData.toBuildingId,
        to_location: formData.isWarehouse ? 'Sklad' : formData.toLocation,
        is_warehouse: formData.isWarehouse,
        assigned_to_id: formData.assignedTo ? formData.assignedTo.id : null,
        requester_transport_option: formData.isWarehouse && formData.transportOption ? formData.transportOption : undefined,
      })

      alert('Žádost o přesun majetku byla úspěšně vytvořena')
      onSuccess()
    } catch (error: any) {
      console.error('Chyba při vytváření žádosti:', error)
      alert(error.message || 'Chyba při vytváření žádosti')
    } finally {
      setSubmitting(false)
    }
  }

  const handleWarehouseToggle = (isWarehouse: boolean) => {
    setFormData({
      ...formData,
      isWarehouse,
      toBuildingId: isWarehouse ? '' : formData.toBuildingId,
      toLocation: isWarehouse ? '' : formData.toLocation,
      assignedTo: isWarehouse ? formData.assignedTo : null,
      transportOption: isWarehouse ? '' : '', // Сбрасываем опцию транспорта
    })
  }

  return (
    <div className="create-property-move-ticket-form-container">
      <div className="create-property-move-ticket-form">
        <h2>Vytvořit žádost o přesun majetku</h2>
        
        <form onSubmit={handleSubmit}>
          {loading ? (
            <div className="loading">Načítání dat...</div>
          ) : (
            <>
              {/* Откуда */}
              <div className="form-group">
                <label htmlFor="fromBuildingId">
                  Odkud (budova) <span className="required">*</span>
                </label>
                <select
                  id="fromBuildingId"
                  value={formData.fromBuildingId}
                  onChange={(e) => setFormData({ ...formData, fromBuildingId: e.target.value })}
                  required
                >
                  <option value="">Vyberte budovu</option>
                  {buildings.map(building => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="fromLocation">
                  Kde konkrétně se nachází objekt/objekty <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="fromLocation"
                  value={formData.fromLocation}
                  onChange={(e) => setFormData({ ...formData, fromLocation: e.target.value })}
                  placeholder="Např. učebna 101, sklad v suterénu..."
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="itemName">
                  Název předmětu <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="itemName"
                  value={formData.itemName}
                  onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                  placeholder="Např. Stůl, Židle, Notebook..."
                  required
                />
              </div>

              <div className="form-group">
                <label>
                  Inventární číslo objektu nebo skupiny objektů <span className="required">*</span>
                </label>
                {formData.inventoryNumbers.map((number, index) => (
                  <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={number}
                      onChange={(e) => updateInventoryNumber(index, e.target.value)}
                      placeholder={`Inventární číslo ${index + 1}`}
                      required={index === 0}
                      style={{ flex: 1 }}
                    />
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => removeInventoryNumberField(index)}
                        style={{
                          padding: '0.5rem',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '1.2rem',
                          width: '2.5rem',
                          height: '2.5rem'
                        }}
                        title="Odstranit pole"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addInventoryNumberField}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                >
                  + Přidat další inventární číslo
                </button>
              </div>

              {/* Куда */}
              <div className="form-group">
                <label>
                  Kam přesunout
                </label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="destinationType"
                      checked={!formData.isWarehouse}
                      onChange={() => handleWarehouseToggle(false)}
                    />
                    <span>Do jiné budovy</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="destinationType"
                      checked={formData.isWarehouse}
                      onChange={() => handleWarehouseToggle(true)}
                    />
                    <span>Sklad</span>
                  </label>
                </div>
              </div>

              {!formData.isWarehouse ? (
                <>
                  <div className="form-group">
                    <label htmlFor="toBuildingId">
                      Kam (budova) <span className="required">*</span>
                    </label>
                    <select
                      id="toBuildingId"
                      value={formData.toBuildingId}
                      onChange={(e) => setFormData({ ...formData, toBuildingId: e.target.value })}
                      required={!formData.isWarehouse}
                    >
                      <option value="">Vyberte budovu</option>
                      {buildings.map(building => (
                        <option key={building.id} value={building.id}>
                          {building.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="toLocation">
                      Kde konkrétně přesunout <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="toLocation"
                      value={formData.toLocation}
                      onChange={(e) => setFormData({ ...formData, toLocation: e.target.value })}
                      placeholder="Např. učebna 205, kancelář 301..."
                      required={!formData.isWarehouse}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Опция транспорта для склада */}
                  <div className="form-group">
                    <label htmlFor="transportOption">
                      Způsob dopravy na sklad <span className="required">*</span>
                    </label>
                    <select
                      id="transportOption"
                      value={formData.transportOption}
                      onChange={(e) => setFormData({ ...formData, transportOption: e.target.value as 'self' | 'organize' })}
                      required={formData.isWarehouse}
                      style={{
                        padding: '0.75rem',
                        fontSize: '1rem',
                        border: '2px solid #3b82f6',
                        borderRadius: '8px',
                        backgroundColor: '#eff6ff'
                      }}
                    >
                      <option value="">Vyberte způsob dopravy...</option>
                      <option value="self">🚗 Přivezu sám na sklad</option>
                      <option value="organize">🚚 Prosím zorganizovat odvoz na sklad</option>
                    </select>
                    <small className="form-hint" style={{ marginTop: '0.5rem', display: 'block', color: '#666' }}>
                      {formData.transportOption === 'self' && '✓ Přivezete majetek na sklad sami'}
                      {formData.transportOption === 'organize' && '✓ Správce majetku zorganizuje odvoz'}
                    </small>
                  </div>
                </>
              )}
              
              {/* Поле "Komu předat" - выпадающий список для склада, автокомплит для других случаев */}
              <div className="form-group">
                <label htmlFor="assignedTo">
                  Komu předat <span className="required">*</span>
                </label>
                {formData.isWarehouse ? (
                  <select
                    id="assignedTo"
                    value={formData.assignedTo?.id || ''}
                    onChange={(e) => {
                      const selectedUser = propertyReferents.find(u => u.id === e.target.value)
                      setFormData({ ...formData, assignedTo: selectedUser || null })
                    }}
                    required
                  >
                    <option value="">Vyberte referenta správy majetku</option>
                    {propertyReferents.map((referent) => (
                      <option key={referent.school_employee_id ?? referent.id} value={referent.id}>
                        {referent.name}
                        {referent.personal_number ? ` (os. č. ${referent.personal_number})` : ''}
                        {referent.email ? ` — ${referent.email}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <UserAutocomplete
                    value={formData.assignedTo}
                    onChange={(user) => setFormData({ ...formData, assignedTo: user })}
                    placeholder="Jméno, e-mail nebo os. číslo…"
                    required={true}
                    employeesOnly
                    requireUserAccount
                    visibleLimit={500}
                  />
                )}
                {formData.isWarehouse ? (
                  <small className="form-hint">
                    Referenti správy majetku ze seznamu zaměstnanců školy
                  </small>
                ) : (
                  <small className="form-hint">
                    Seznam zaměstnanců školy s účtem v intranetu — klikněte do pole pro celý seznam nebo pište pro vyhledání
                  </small>
                )}
              </div>
            </>
          )}

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-button">
              Zrušit
            </button>
            <button type="submit" disabled={submitting} className="submit-button">
              {submitting ? 'Vytváření...' : 'Vytvořit žádost'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreatePropertyMoveTicketForm

